import { useEffect, useState, useSyncExternalStore } from "react";
import {
  fetchRbacToken,
  getMfaStateSnapshot,
  subscribeToMfaState,
  userLoggedInFacilityType,
} from "../services/memberManagementService";
import type { AuthError, MfaStatus, RbacTokenErrorType } from "../types";

interface UseRbacTokenResult {
  isLoaded: boolean;
  hasNoFacilityAssigned: boolean;
  authError: AuthError | null;
  mfaStatus: MfaStatus;
}

function mapRbacErrorToAuthError(
  errorType: RbacTokenErrorType,
  errorMessage: string | undefined,
): AuthError {
  let title = "Authentication Error";
  let message = errorMessage ?? "An error occurred. Please try again.";

  switch (errorType) {
    case "inactive_account":
      title = "Account Inactive";
      break;
    case "unauthorized":
      title = "Session Expired";
      message =
        errorMessage ?? "Your session has expired. Please sign in again.";
      break;
    case "forbidden":
      title = "Access Denied";
      break;
    default:
      title = "Authentication Error";
  }

  return { type: errorType, title, message };
}

export function useRbacToken(
  facilityId: string | undefined,
): UseRbacTokenResult {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasNoFacilityAssigned, setHasNoFacilityAssigned] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);

  const mfa = useSyncExternalStore(
    subscribeToMfaState,
    getMfaStateSnapshot,
    getMfaStateSnapshot,
  );

  useEffect(() => {
    setIsLoaded(false);
    setAuthError(null);
    setHasNoFacilityAssigned(false);

    // While a second-factor challenge is pending, the challenge screen owns the
    // flow. Don't attempt a token fetch (it would just re-issue the challenge).
    if (mfa.status) {
      return;
    }

    const loadRbacToken = async () => {
      if (!facilityId || facilityId.trim() === "") {
        return;
      }

      const result = await fetchRbacToken(facilityId);

      if (result.success) {
        setIsLoaded(true);

        const facilityType = userLoggedInFacilityType(facilityId);
        if (facilityType === "not_assigned") {
          setHasNoFacilityAssigned(true);
        }
        return;
      }

      // A pending MFA challenge surfaces as a non-success fetch with no error
      // type; render the challenge instead of an error page.
      if (getMfaStateSnapshot().status) {
        return;
      }

      console.warn("Failed to fetch RBAC token:", result.errorMessage);
      const errorType = result.errorType ?? "unknown";
      const error = mapRbacErrorToAuthError(errorType, result.errorMessage);
      setAuthError(error);
    };

    if (facilityId) {
      loadRbacToken();
    }
  }, [facilityId, mfa.status]);

  return { isLoaded, hasNoFacilityAssigned, authError, mfaStatus: mfa.status };
}
